import Emoji from "@core-components/emoji/Emoji";
import SSHeadline from "@core-components/title/SSHeadline";
import SSLoLGradientText from "@core-components/title/components/SSLoLGradientText";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import EPublicRoutes from "@core-types/routes.enum";
import { useAppNavigate } from "@hooks/language/useAppNavigate";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, Grid, Stack } from "@mantine/core";
import AffiliateLinkCard from "@modules/clientDashboard/components/AffiliateLinkCard";
import CurrentStepProgressCard from "@modules/clientDashboard/components/CurrentStepProgressCard";
import NextUserRewardCard from "@modules/clientDashboard/components/NextUserRewardCard";
import { AppNotification } from "@notifications/Notification";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { OrderAPI } from "../../../api/Order.api";
import AccountCredentials from "../components/after-buy/AccountCredientials";
import HowToChangeYourRiotId from "../components/after-buy/HowToChangeYourRiotId";
import HowToSecureYourAccount from "../components/after-buy/HowToSecureYourAccount";
import HowToUnlockSkin from "../components/after-buy/HowToUnlockSkin";
import OrderDetails from "../components/after-buy/OrderDetails";
import { useOrderItemAsBasketAccount } from "../hooks/useBasketItem.hook";
import type IOrder from "../models/IOrder";
import { useBasketStore } from "../stores/Basket.store";

// used in sub-components
export const BOLD_TEXT_CLASS_NAME =
  "text-primary-white font-secret font-semibold";
export const LIGHT_TEXT_CLASS_NAME =
  "text-primary-white font-inter font-extralight";

export const OrderTracking = () => {
  const { t } = useAppTranslation("pages.order.tracking");
  const { navigate } = useAppNavigate();
  const [order, setOrder] = useState<IOrder | undefined>(undefined);

  const basket = useBasketStore();

  const updateOrder = () => {
    if (!order?.token) return;

    OrderAPI.cancelOrder(order.token).then(() => {
      recreateOrder();
    });
  };

  const recreateOrder = () => {
    if (!order?.accounts) return;

    basket.addBasketAccounts(order.accounts.map(useOrderItemAsBasketAccount));

    AppNotification.default({
      title: t("notifications.orderRecreated.title"),
      message: t("notifications.orderRecreated.message"),
    });

    navigate(EPublicRoutes.BASKET);
  };

  const cancelOrder = () => {
    if (!order?.token) return;

    OrderAPI.cancelOrder(order?.token).then(() => {
      AppNotification.default({
        title: t("notifications.orderCancelled.title"),
        message: t("notifications.orderCancelled.message"),
      });
      navigate(EPublicRoutes.HOME);
    });
  };

  const { token } = useParams();

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate is making infinite loop because of its internal memoization
  useEffect(() => {
    if (!token) {
      return;
    }

    OrderAPI.getOrderDetails(token)
      .then((res) => setOrder(res.data))
      .catch(() => navigate(EPublicRoutes.HOME));
  }, [token]);

  return (
    <Stack align="center" className="min-h-screen" justify="flex-start">
      <Flex
        className="mt-12 xs:mt-28 mb-5 w-full sm:mt-24 sm:mb-10"
        justify="flex-start"
      >
        <SSHeadline
          props={{
            title: { fz: 30 },
            stack: { align: "start" },
          }}
        >
          <Emoji.Party size={30} />
          <SSLoLGradientText> {t("banner.thanks")} </SSLoLGradientText>
          <SSWhiteText>{t("banner.forPurchase")}</SSWhiteText>
        </SSHeadline>
      </Flex>
      <Grid columns={24} gutter={{ base: 30, sm: 20 }}>
        <Grid.Col span={{ base: 24, sm: 14, md: 14 }}>
          <AccountCredentials order={order} />
        </Grid.Col>
        <Grid.Col span={"auto"}>
          <OrderDetails
            order={order}
            updateOrder={updateOrder}
            cancelOrder={cancelOrder}
            recreateOrder={recreateOrder}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 24, sm: 14, md: 14 }}>
          <Stack className="gap-8 sm:gap-5">
            <HowToSecureYourAccount />
            <HowToChangeYourRiotId />
            <HowToUnlockSkin />
          </Stack>
        </Grid.Col>
        <Grid.Col span={"auto"} className="space-y-4">
          <NextUserRewardCard
            containerProps={{ fullWidth: true }}
            displayButton
            coinAmount={order?.coinsGained}
          />
          <CurrentStepProgressCard coinAmount={order?.coinsGained} />
          <AffiliateLinkCard
            affiliateCode={order?.affiliateCode}
            containerProps={{ fullWidth: true }}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
